import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BusyButton, Field, PageHeader, useToast } from "../../components/ui";
import { DirtyActions } from "../../components/UnsavedChanges";
import { ErrorState, Skeleton } from "../../components/States";
import { useAuth } from "../../auth/store";
import { formatDateTime } from "../../lib/format";
import { mailAdminApi, mailAdminKeys, type MailConfig, type MailConfigInput, type MailProvider } from "./api";
import "./mail-admin.css";

const PROVIDERS: Array<{ id: Exclude<MailProvider, "log">; label: string; hint: string }> = [
  { id: "resend", label: "Resend", hint: "An API key from resend.com. The sending domain must be verified there first." },
  { id: "smtp", label: "SMTP", hint: "Any relay: Postmark, SES SMTP, Google Workspace, your own server." },
  { id: "ses", label: "Amazon SES", hint: "Uses the AWS credentials already available to the API process." },
];

type Form = MailConfigInput;

function sendingProvider(p: MailProvider | undefined): Exclude<MailProvider, "log"> {
  return p === "smtp" || p === "ses" || p === "resend" ? p : "resend";
}

/** Settings are sending when they were saved here or came with the deployment. */
function inUse(c: MailConfig) {
  return (c.configured || c.source === "deployment") && c.provider !== "log" && !!c.provider;
}

function formFrom(c: MailConfig): Form {
  const configured = inUse(c);
  return {
    provider: sendingProvider(c.provider),
    from: configured ? c.from : "",
    smtpHost: configured ? (c.smtpHost ?? "") : "",
    smtpPort: configured ? c.smtpPort : undefined,
    smtpUser: configured ? (c.smtpUser ?? "") : "",
    smtpTls: configured ? (c.smtpTls ?? "starttls") : "starttls",
    resendApiKey: "",
    smtpPassword: "",
  };
}

function same(a: Form, b: Form) {
  return (
    a.provider === b.provider &&
    a.from === b.from &&
    (a.smtpHost ?? "") === (b.smtpHost ?? "") &&
    (a.smtpPort ?? 0) === (b.smtpPort ?? 0) &&
    (a.smtpUser ?? "") === (b.smtpUser ?? "") &&
    (a.smtpTls ?? "starttls") === (b.smtpTls ?? "starttls") &&
    !a.resendApiKey &&
    !a.smtpPassword
  );
}

/**
 * Where a deployment chooses how it sends mail. Every account-lifecycle message
 * goes through this provider — verification links, password resets, invitations,
 * notification email.
 *
 * Settings live in the database. Until someone saves a provider here, the
 * provider the deployment started with sends; with none, mail is logged and
 * nothing is delivered.
 */
export function MailAdminPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const auth = useAuth();
  const cfg = useQuery({ queryKey: mailAdminKeys.config, queryFn: mailAdminApi.config });
  const [form, setForm] = useState<Form | null>(null);
  const [to, setTo] = useState("");

  useEffect(() => {
    if (cfg.data) setForm((f) => f ?? formFrom(cfg.data));
  }, [cfg.data]);
  useEffect(() => {
    if (!to && auth.me?.user.email) setTo(auth.me.user.email);
  }, [auth.me?.user.email, to]);

  const save = useMutation({
    mutationFn: (body: Form) => mailAdminApi.save(body),
    onSuccess: (next) => {
      qc.setQueryData(mailAdminKeys.config, next);
      setForm(formFrom(next));
      toast.push("success", "Mail settings saved. All services pick them up within a minute.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const test = useMutation({
    mutationFn: () => mailAdminApi.test(to, form!),
    onSuccess: () => toast.push("success", `Test email sent to ${to}.`),
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const data = cfg.data;
  if (!data || !form) {
    return <Shell>{cfg.isError ? <ErrorState error={cfg.error} retry={() => void cfg.refetch()} /> : <Skeleton lines={6} />}</Shell>;
  }

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f!, ...patch }));
  const provider = PROVIDERS.find((p) => p.id === form.provider)!;
  const active = inUse(data);
  const fromDeployment = active && !data.configured;
  const dirty = !same(form, formFrom(data));
  const credentialReady =
    form.provider === "ses" ||
    (form.provider === "resend" && (!!form.resendApiKey || (data.hasCredential && data.provider === "resend"))) ||
    (form.provider === "smtp" && !!form.smtpHost);
  // Deployment settings can be saved as they are, so they stay after the next
  // start even if the deployment changes.
  const canSave = (dirty || fromDeployment) && credentialReady && !!form.from.trim();

  return (
    <Shell>
      <div className="card section">
        <div className="mail-admin-head">
          <div>
            <h2>Provider</h2>
            <p className="muted small">
              Verification links, password resets, invitations, and notification email all go through this provider.
            </p>
          </div>
          <span className={`badge ${active ? "ok" : "warn"}`} data-testid="mail-source">
            {active ? `In use: ${label(data.provider)}` : "Not configured"}
          </span>
        </div>

        {!active && (
          <p className="muted small" data-testid="mail-unconfigured-note">
            Nothing is delivered until you save a provider.
          </p>
        )}
        {fromDeployment && (
          <p className="muted small" data-testid="mail-deployment-note">
            These settings came with the deployment. Save them to keep them, or change them here; what you save here is what sends.
          </p>
        )}

        <div className="mail-providers" role="group" aria-label="Mail provider">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={"setup-provider" + (form.provider === p.id ? " selected" : "")}
              onClick={() => set({ provider: p.id })}
              aria-pressed={form.provider === p.id}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="muted small">{provider.hint}</p>

        <div className="settings-stack">
          <Field label="From address" hint="Must be on a domain your provider has verified.">
            {(p) => (
              <input {...p} className="input" value={form.from} onChange={(e) => set({ from: e.target.value })} placeholder="Backstory <no-reply@example.com>" />
            )}
          </Field>

          {form.provider === "resend" && (
            <Field
              label="Resend API key"
              hint={data.hasCredential && data.provider === "resend" ? "A key is stored. Leave this blank to keep it." : "Starts with re_. Create it under API Keys in Resend."}
            >
              {(p) => (
                <input
                  {...p}
                  className="input mono"
                  type="password"
                  value={form.resendApiKey ?? ""}
                  onChange={(e) => set({ resendApiKey: e.target.value })}
                  placeholder={data.hasCredential && data.provider === "resend" ? "••••••••••••" : "re_…"}
                />
              )}
            </Field>
          )}

          {form.provider === "smtp" && (
            <>
              <div className="mail-smtp-row">
                <Field label="Host">
                  {(p) => <input {...p} className="input" value={form.smtpHost ?? ""} onChange={(e) => set({ smtpHost: e.target.value })} placeholder="smtp.example.com" />}
                </Field>
                <Field label="Port">
                  {(p) => (
                    <input
                      {...p}
                      className="input"
                      inputMode="numeric"
                      value={form.smtpPort ?? ""}
                      onChange={(e) => set({ smtpPort: Number(e.target.value) || undefined })}
                      placeholder="587"
                    />
                  )}
                </Field>
              </div>
              <div className="mail-smtp-row pair">
                <Field label="Username">
                  {(p) => <input {...p} className="input" value={form.smtpUser ?? ""} onChange={(e) => set({ smtpUser: e.target.value })} />}
                </Field>
                <Field label="Encryption">
                  {(p) => (
                    <select {...p} className="input" value={form.smtpTls ?? "starttls"} onChange={(e) => set({ smtpTls: e.target.value as Form["smtpTls"] })}>
                      <option value="starttls">STARTTLS</option>
                      <option value="implicit">Implicit TLS</option>
                      <option value="none">None</option>
                    </select>
                  )}
                </Field>
              </div>
              <Field label="Password" hint={data.hasCredential && data.provider === "smtp" ? "A password is stored. Leave this blank to keep it." : undefined}>
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    type="password"
                    value={form.smtpPassword ?? ""}
                    onChange={(e) => set({ smtpPassword: e.target.value })}
                    placeholder={data.hasCredential && data.provider === "smtp" ? "••••••••••••" : undefined}
                  />
                )}
              </Field>
            </>
          )}

          <DirtyActions dirty={dirty} onDiscard={() => setForm(formFrom(data))}>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" onClick={() => save.mutate(form)} disabled={!canSave}>
              Save mail settings
            </BusyButton>
          </DirtyActions>
          {data.configured && data.configuredAt && <p className="muted small">Last changed {formatDateTime(data.configuredAt)}.</p>}
        </div>
      </div>

      <div className="card section">
        <h2>Send a test</h2>
        <p className="muted small">Uses the settings above, saved or not, so a bad key fails here instead of on a password reset.</p>
        <div className="mail-test">
          <Field label="To">
            {(p) => <input {...p} className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
          <BusyButton className="btn" busy={test.isPending} busyLabel="Sending…" onClick={() => test.mutate()} disabled={!to.includes("@") || !credentialReady || !form.from.trim()}>
            Send test email
          </BusyButton>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mail-admin">
      <PageHeader title="Email" subtitle="How this deployment sends mail" />
      {children}
    </div>
  );
}

function label(p: MailProvider) {
  return PROVIDERS.find((x) => x.id === p)?.label ?? p;
}
